/**
 * Discharge Control Operations
 *
 * This module contains the main discharge control functions including
 * discharge initiation, hold, resume, and finish operations.
 */

import type { IBatchStore } from '../batch-store-types';
import { DeviceStatus } from '../../../constants/DeviceStatus';
import { BatchAction } from '../../../constants/MaterialModels';
import { BatchCycleStatus } from '../../../constants/BatchCycleStatus';
import { DeviceTypes } from '../../../constants/DeviceModels';
import logger from '../../../utils/logger';
import { timerKey } from '../../../utils/timer-manager/timer-manager';
import { dustCollectorSwitch } from '../device-switches/dust-collector-switch';
import { bootSwitch } from '../device-switches/boot-switch';
import {
  isReadyForFinalDischarge,
  mixerIsReadyForDischarge,
  isBootDeviceReadyForDischarge,
} from './discharge-readiness';
import { Device } from '../../../../types/api/Device';
import { MixingStartPoint } from '../../../constants/BatchTypeEnums';

/**
 * Main discharge function
 * Initiates the discharge process or toggles hold if already discharging
 *
 * @param batchStore - The batch store instance
 * @param manual - Whether discharge was triggered manually (button press)
 */
export function discharge(batchStore: IBatchStore, manual = false): void {
  const { settingsStore } = batchStore.rootStore;
  const { configuration } = settingsStore;

  logger.log(`DISCHARGE: isDischarge: ${batchStore.isDischarge}, discharging: ${batchStore.discharging},
    isHoldDischarge: ${batchStore.isHoldDischarge}, isEmergencyStop: ${batchStore.isEmergencyStop}`);
  batchStore.saveLog(`DISCHARGE ${manual ? 'key pressed' : ''}`);

  if (batchStore.isAbort) {
    return;
  }

  if (batchStore.isEmergencyStop) {
    return;
  }

  if (manual) {
    batchStore.context.onBatchCycle(BatchCycleStatus.DISCHARGE_BUTTON_PRESS, batchStore.inputs, batchStore.outputs, batchStore.channels);
  }

  if (batchStore.discharging && manual) {
    holdDischarge(batchStore);
    return;
  }

  batchStore.timers.clear(timerKey.discharge());

  batchStore.isDischarge = manual && !batchStore.discharging ? !batchStore.isDischarge : true;

  let runningData = batchStore.running.length ? batchStore.running[0] : null;

  if (isMultiBatchMixerReadyForAnotherDrop(batchStore, runningData)) {
    runningData = batchStore.running[1];
  }

  if (!runningData || !batchStore.isDischarge) {
    return;
  }

  if (
    configuration?.dischargeAuthorizationInput
    && !runningData.dischargeAuthorization
    && !batchStore.isTestMode
  ) {
    checkDischargeAuthorizationInput(batchStore);
    return;
  }

  batchStore.updateSequenceTargets(runningData);

  batchStore.dischargeWaterIfReady(runningData);

  if (!isReadyForFinalDischarge(batchStore, runningData)) {
    if (!runningData.mixerId) {
      batchStore.timers.set(timerKey.discharge(), () => {
        if (batchStore.isDischarge) {
          discharge(batchStore);
        }
      }, 2000, 'Discharge - not ready for final discharge');
      return;
    }

    const ms = batchStore.getDeviceStatus(runningData.mixerId);

    if (runningData.uid === ms.uid && runningData.currentDrop === ms.currentDrop && ms.status !== DeviceStatus.IDLE && batchStore.running.length > 1) {
      runningData = batchStore.running[1];

      if (!isReadyForFinalDischarge(batchStore, runningData)) {
        batchStore.timers.set(timerKey.discharge(), () => {
          if (batchStore.isDischarge) {
            discharge(batchStore);
          }
        }, 2000, 'Discharge - mixer not ready');
        return;
      }

    } else {
      batchStore.timers.set(timerKey.discharge(), () => {
        if (batchStore.isDischarge) {
          discharge(batchStore);
        }
      }, 2000, 'Discharge - checking status');
      return;
    }
  }

  if (batchStore.isHoldDischarge || runningData.isHoldLoad) {
    batchStore.timers.set(timerKey.discharge(), () => {
      if (batchStore.isDischarge) {
        discharge(batchStore);
      }
    }, 2000, 'Discharge - hold discharge');
    return;
  }

  if (runningData.finalScales && runningData.finalScalesDischarged.length === Object.keys(runningData.finalScales).length) {
    logger.log('runningData already discharged:', runningData);
    return;
  }

  if (!mixerIsReadyForDischarge(batchStore, runningData)) {
    batchStore.timers.set(timerKey.discharge(), () => {
      if (batchStore.isDischarge) {
        discharge(batchStore);
      }
    }, 2000, 'Discharge - mixer not ready');
    return;
  }

  if (!isBootDeviceReadyForDischarge(batchStore)) {
    batchStore.timers.set(timerKey.discharge(), () => {
      if (batchStore.isDischarge) {
        discharge(batchStore);
      }
    }, 2000, 'Discharge - boot not ready');

    return;
  }

  if (isMultiBatchMixerReadyForAnotherDrop(batchStore, batchStore.running[0])) {
    batchStore.finishBatch(batchStore.running[0]);
  }

  if (runningData?.pumps && Object.keys(runningData.pumps).length) {
    batchStore.onBatchAction(runningData, BatchAction.StartDischarging);

    if (!runningData.headWater) {
      batchStore.onBatchAction(runningData, BatchAction.StartDischargingHeadWater);
    }

    if (!runningData.washWater) {
      batchStore.onBatchAction(runningData, BatchAction.StartDischargingWashWater);
    }

    if (!runningData.scales) {
      batchStore.onBatchAction(runningData, BatchAction.ScaleAggDischargeStart);
      batchStore.onBatchAction(runningData, BatchAction.ScaleCemDischargeStart);
      batchStore.onBatchAction(runningData, BatchAction.ScaleWaterDischargeStart);
      batchStore.onBatchAction(runningData, BatchAction.ScaleAggDischargeComplete);
      batchStore.onBatchAction(runningData, BatchAction.ScaleCemDischargeComplete);
      batchStore.onBatchAction(runningData, BatchAction.ScaleWaterDischargeComplete);
    }
  }

  if (runningData.finalScales && batchStore.waitingForDischarge.length) {
    batchStore.dischargeWithSequence(runningData);
  }

  if (runningData.mixerId) {
    const mixer = batchStore.devicesStore.getDeviceById(Number(runningData.mixerId));
    if (mixer) {
      const { uid, currentDrop, allDrops, tId, truckCode } = runningData;

      const temperWaterData = batchStore.getTemperWaterData(mixer);
      runningData.temperWaterStart = batchStore.getTemperWater(mixer) * temperWaterData.meterRate;
      runningData.temperWaterUnitSymbol = temperWaterData.unitSymbol;
      runningData.temperWaterUnitId = temperWaterData.unitId;
      runningData.temperWaterPNo = mixer.tWaterMeterStatusPNo;
      logger.log('start temper water', runningData.temperWaterStart, runningData.temperWaterUnitSymbol, mixer.temperWaterPNo);

      runningData.mixerFeedingStartTime = Date.now();
      logger.log('mixer feeding start', mixer.mixingStartPoint);

      batchStore.deviceStatus.set(mixer.id, {
        ...batchStore.getDeviceStatus(mixer.id),
        id: mixer.id,
        status: mixer.mixingStartPoint === MixingStartPoint.OnFirstMaterialLoaded ? DeviceStatus.START_MIXING : DeviceStatus.FEED,
        restart: true,
        delay: 0,
        uid,
        currentDrop,
        allDrops,
        tId,
        truckCode,
      });
    }
  }
}

/**
 * Hold/pause discharge operation
 * Toggles the discharge hold state and resumes if already held
 *
 * @param batchStore - The batch store instance
 */
export function holdDischarge(batchStore: IBatchStore): void {
  if (batchStore.canSendRemoteInstruction()) {
    return;
  }

  batchStore.isHoldDischarge = !batchStore.isHoldDischarge;
  if (!batchStore.isHoldDischarge && !batchStore.isEmergencyStop) {
    resumeDischarge(batchStore);
  }
}

/**
 * Resume discharge operation after hold
 * Resumes all tracked discharge scales
 *
 * @param batchStore - The batch store instance
 */
export function resumeDischarge(batchStore: IBatchStore): void {
  if (batchStore.canSendRemoteInstruction()) {
    return;
  }

  if (batchStore.trackedDischargeScales.length) {
    batchStore.trackedDischargeScales.forEach(id => batchStore.resumeScale(id));
    batchStore.trackedDischargeScales = [];
  }
}

/**
 * Finish discharge operation
 * Cleans up discharge state and moves to next batch phase
 *
 * @param batchStore - The batch store instance
 * @param runningData - The current batch data
 */
export function finishDischarge(batchStore: IBatchStore, runningData: any): void {
  const data = { ...runningData };
  const dustCollector = batchStore.devicesStore.devices.find((v: Device) => v.deviceTypeName === DeviceTypes.DustCollector);
  if ((dustCollector && !dustCollector.keepOnBetweenLoads) || (batchStore.running.length === 1 && !batchStore.queue.length)) {
    dustCollectorSwitch(batchStore.rootStore, false);
  }
  batchStore.discharging = false;

  if (data.currentDrop === data.allDrops) {
    bootSwitch(batchStore.rootStore, () => {
      batchStore.checkBeltsRunning(data, () => {
        batchStore.finishBatch(data);
      });
    });
  } else {
    batchStore.checkBeltsRunning(data, () => {
      batchStore.finishBatch(data);
    });
  }
}

/**
 * Finish discharge scale earlier than target
 * Marks scale as finished and removes from tracking
 *
 * @param batchStore - The batch store instance
 * @param scaleId - The scale ID to finish
 */
export function finishDischargeScaleEarlier(batchStore: IBatchStore, scaleId: number): void {
  if (batchStore.canSendRemoteInstruction()) {
    return;
  }

  const d = batchStore.getDeviceStatus(scaleId);
  const initialData = d?.uid ? batchStore.running.find((v: any) => v.uid === d.uid && v.currentDrop === d.currentDrop) : null;

  if (initialData) {
    batchStore.deviceStatus.set(scaleId, {
      ...d,
      status: DeviceStatus.FINISH,
    });

    batchStore.trackedDischargeScales = batchStore.trackedDischargeScales.filter(id => id !== scaleId);
    initialData.scalesFinishedDischargeEarlier.push(scaleId);
  }
}

/**
 * Check discharge authorization input
 * Verifies authorization before allowing discharge
 *
 * @param batchStore - The batch store instance
 */
function checkDischargeAuthorizationInput(batchStore: IBatchStore): void {
  const { settingsStore } = batchStore.rootStore;
  const { configuration } = settingsStore;
  const dischargeAuthorization = batchStore.running?.length ? batchStore.running[0].dischargeAuthorization : false;

  batchStore.timers.clear(timerKey.dischargeAuthorization());

  if (
    configuration?.dischargeAuthorizationInput
    && !dischargeAuthorization
    && batchStore.isDischarge
  ) {
    logger.log(`DISCHARGE: waiting for authorization input: ${configuration?.dischargeAuthorizationInput}`);
    batchStore.showWarning('Discharge is waiting for authorization');

    batchStore.timers.set(timerKey.dischargeAuthorization(), () => {
      if (batchStore.isDischarge) {
        checkDischargeAuthorizationInput(batchStore);
      }
    }, 2000, 'Check discharge authorization');
  } else {
    discharge(batchStore);
  }
}

/**
 * Check if multibatch mixer is ready for another drop
 * Determines if mixer can accept next drop while still mixing current
 *
 * @param batchStore - The batch store instance
 * @param runningData - The current batch data
 * @returns true if ready for another drop, false otherwise
 */
function isMultiBatchMixerReadyForAnotherDrop(batchStore: IBatchStore, runningData: any): boolean {
  if (batchStore.running.length > 1 && runningData?.mixerId) {
    const mixer = batchStore.devicesStore.getDeviceById(Number(runningData.mixerId));
    if (mixer?.multibatch) {
      const ms = batchStore.getDeviceStatus(runningData.mixerId);

      if (
        runningData.uid === ms.uid
        && runningData.currentDrop === ms.currentDrop
        && runningData.allDrops !== ms.currentDrop
        && ms.status === DeviceStatus.MIXING
      ) {
        logger.log(`multibatch mixer: ${mixer.description} ${ms.status}
        ready for another drop ${ms.currentDrop+1}/${runningData.allDrops}`);
        return true;
      }
    }
  }

  return false;
}
